import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from "@angular/common/http";
import {
  enableProdMode,
  EnvironmentProviders,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  Provider,
  provideZoneChangeDetection,
} from "@angular/core";
import { bootstrapApplication } from "@angular/platform-browser";
import {
  provideRouter,
  RouteReuseStrategy,
  withDisabledInitialNavigation,
  withHashLocation,
  withPreloading,
} from "@angular/router";
import { Clipboard } from "@awesome-cordova-plugins/clipboard/ngx";
import { Deeplinks } from "@awesome-cordova-plugins/deeplinks/ngx";
import { Device } from "@awesome-cordova-plugins/device/ngx";
import { Facebook } from "@awesome-cordova-plugins/facebook/ngx";
import { File } from "@awesome-cordova-plugins/file/ngx";
import { Firebase } from "@awesome-cordova-plugins/firebase/ngx";
import { Geolocation } from "@awesome-cordova-plugins/geolocation/ngx";
import { Keyboard } from "@awesome-cordova-plugins/keyboard/ngx";
import { LineLogin } from "@awesome-cordova-plugins/line-login/ngx";
import { Push } from "@awesome-cordova-plugins/push/ngx";
import { SocialSharing } from "@awesome-cordova-plugins/social-sharing/ngx";
import { SplashScreen } from "@awesome-cordova-plugins/splash-screen/ngx";
import { StatusBar } from "@awesome-cordova-plugins/status-bar/ngx";
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from "@ionic/angular/standalone";
import { IonicConfig } from "@ionic/core";
import { IonicStorageModule } from "@ionic/storage-angular";
import { appRoutes } from "./app/app-routing";
import { ForceReloadInterceptor } from "./app/api/maintenance/force-reload.interceptor";
import { AuthRefreshInterceptor } from "./app/api/base/auth-refresh.interceptor";
import { AppComponent } from "./app/app.component";
import { AppConfig } from "./app/app.config";
import { GlobalErrorHandler } from "./app/services/global-error-handler/global-error-handler";
import { SelectivePreloadingStrategy } from "./app/services/routing/selective-preloading-strategy";
import { environment } from "./environments/environment";

if (environment.production) {
  enableProdMode();
}
/**
 * pato共通のionicConfig
 * - `IonicModule`や`provideIonicAngular`に渡す値
 */
export const PatoClientIonicConfig: IonicConfig = {
  mode: "ios",
  rippleEffect: false,
  innerHTMLTemplatesEnabled: true,
};

/**
 * pato共通のproviders
 * - `bootstrapApplication.providers`に渡す値
 */
export const PatoClientProviders: (Provider | EnvironmentProviders)[] = [
  { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
  provideIonicAngular(PatoClientIonicConfig),
  provideRouter(
    appRoutes,
    withHashLocation(),
    withDisabledInitialNavigation(),
    withPreloading(SelectivePreloadingStrategy)
  ),
  Facebook,
  Firebase,
  Storage,
  Push,
  StatusBar,
  SplashScreen,
  Device,
  Deeplinks,
  Geolocation,
  Keyboard,
  Clipboard,
  File,
  LineLogin,
  { provide: ErrorHandler, useClass: GlobalErrorHandler },
  { provide: LOCALE_ID, useValue: "ja-JP" },
  SocialSharing,
  provideHttpClient(withXhr(), withInterceptorsFromDi()),
  // APIレスポンスの強制再起動ヘッダ(X-Ckuru-Force-Reload / X-Ckuru-User-Force-Reload)を処理する
  { provide: HTTP_INTERCEPTORS, useClass: ForceReloadInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: AuthRefreshInterceptor, multi: true },

  // TODO: 各moduleのstandalone化
  importProvidersFrom(IonicStorageModule.forRoot(AppConfig.STORAGE_SETTING)),
];

// 3Dセキュア/Liquid eKYCのiframe内で復帰URL(res3dsecure/from_ekyc付き)がロードされた場合、
// SPAを起動しない。起動すると親と同一セッションでtabs.tsの復帰処理(logout等)が走り
// 親セッションを破壊する (issue #812 / #812拡張: iframe_ekyc)。
const isInIframe = window.self !== window.top;
const is3dSecureResume = /[?&]res3dsecure=/.test(window.location.href);
const isEkycResume = /[?&]from_ekyc=/.test(window.location.href);

if (isInIframe && is3dSecureResume) {
  window.parent.postMessage(
    { type: "pato-3ds-resume", href: window.location.href },
    window.location.origin
  );
} else if (isInIframe && isEkycResume) {
  window.parent.postMessage(
    { type: "pato-ekyc-resume", href: window.location.href },
    window.location.origin
  );
} else {
  bootstrapApplication(AppComponent, {
    providers: [provideZoneChangeDetection(), ...PatoClientProviders],
  });
}
